import {
  WBBox,
  WBButton,
  WBFlex,
  WBCheckbox,
  WBSplitButton,
} from '@admiin-com/ds-web';
import { useTranslation } from 'react-i18next';
import React, { useState, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { BottomDrawer } from './BottomDrawer';
import {
  TaskDirection,
  TaskType,
  TaskSettlementStatus,
} from '@admiin-com/ds-graphql';
import { useTaskCreationContext } from '../../pages/TaskCreation';
import { isAllSigned } from '../../helpers/signature';
import { styled } from '@mui/material';
import { useClientFirmUsers } from '../../hooks/useClientFirmUsers/useClientFirmUsers';

const ButtonWrapper = styled(WBFlex)(({ theme }) => ({
  flexDirection: 'row',
  width: '100%',
  gap: theme.spacing(1),
}));

export const DocumentCTAButtons = React.forwardRef(
  (
    {
      handleNext,
      handleDraft,
      handleSaveForReview,
      handleBackToEdit,
      disabled,
      loading,
      nextButtonText,
      defaultConfirmationChecked,
    }: {
      handleBackToEdit?: () => void;
      handleDraft?: () => Promise<void>;
      handleSaveForReview?: () => Promise<void>;
      handleNext: (data: any) => Promise<void>;
      disabled: boolean;
      loading: boolean;
      nextButtonText?: string;
      defaultConfirmationChecked?: boolean;
    },
    ref
  ) => {
    const { setValue } = useFormContext();
    const { t } = useTranslation();
    const annotations = useWatch({ name: 'annotations' });
    const [saveLoading, setSaveLoading] = useState(false);
    const [confirmationChecked, setConfirmationChecked] = useState(
      defaultConfirmationChecked ?? false
    );
    const { taskDirection, task, client } = useTaskCreationContext();
    const allSigned = isAllSigned(annotations);
    const handleSave = async (action?: () => Promise<void>) => {
      if (!action) return;
      try {
        setSaveLoading(true);
        setValue('type', TaskType.SIGN_ONLY);
        await action();
      } finally {
        setSaveLoading(false);
      }
    };
    const onSaveAsDraft = () => handleSave(handleDraft);
    const onSaveForReview = () => handleSave(handleSaveForReview);
    const { firmEntityUser } = useClientFirmUsers(client);
    const hasManagerOrPartner =
      !firmEntityUser?.managerId && !firmEntityUser?.partnerId;
    const saveForReviewTooltip = t('managerOrPartnerRequiredToSaveForReview', {
      ns: 'taskbox',
    });
    const futureTaskInput = useWatch({ name: 'futureTaskInput' });

    const createButtonText = t(
      taskDirection === TaskDirection.RECEIVING
        ? allSigned
          ? futureTaskInput
            ? 'Create & Sign'
            : 'sign'
          : task
          ? 'send'
          : 'create'
        : 'send',
      {
        ns: 'taskbox',
      }
    );

    const { showConfirmationCheckbox, confirmationMessage } = useMemo(() => {
      const shouldShowCheckbox =
        !!futureTaskInput?.from &&
        taskDirection === TaskDirection.RECEIVING &&
        futureTaskInput?.settlementStatus === TaskSettlementStatus.PAYABLE;
      const isBpayReference =
        futureTaskInput?.from?.metadata?.billerCode === 75556;
      const message = isBpayReference
        ? t('confirmPaymentReferenceNumberCorrect', {
            ns: 'taskbox',
            defaultValue: 'I confirm the Payment Reference Number is correct',
          })
        : t('confirmPaymentDetailsCorrect', {
            ns: 'taskbox',
            defaultValue: 'I confirm the Payment Details are correct',
          });

      return {
        showConfirmationCheckbox: shouldShowCheckbox,
        confirmationMessage: message,
      };
    }, [
      futureTaskInput?.from,
      futureTaskInput?.settlementStatus,
      taskDirection,
      t,
    ]);
    return (
      <>
        <WBFlex
          flexDirection={'column'}
          width={'auto'}
          display={['none', 'flex']}
          mb={3}
        >
          {showConfirmationCheckbox && (
            <WBBox textAlign="right" mb={2}>
              <WBCheckbox
                label={confirmationMessage}
                size="small"
                checked={confirmationChecked}
                onChange={(e) => setConfirmationChecked(e.target.checked)}
                name="confirmDetails"
              />
            </WBBox>
          )}

          <WBFlex
            flexDirection={'row'}
            justifyContent={'space-between'}
            alignItems={'center'}
            width="100%"
          >
            <WBBox>
              {handleBackToEdit && (
                <WBButton
                  variant="outlined"
                  sx={{ px: 3 }}
                  autoFocus={false}
                  type="button"
                  onClick={handleBackToEdit}
                >
                  {t('backToEdit', { ns: 'taskbox' })}
                </WBButton>
              )}
            </WBBox>
            <WBFlex gap={3} alignItems={'center'}>
              {handleDraft && handleSaveForReview ? (
                <WBSplitButton
                  mainAction={{
                    label: t('saveForReview', { ns: 'taskbox' }),
                    onClick: onSaveForReview,
                  }}
                  options={[
                    {
                      label: t('saveForReview', { ns: 'taskbox' }),
                      onClick: onSaveForReview,
                      disabled: false,
                      tooltip: hasManagerOrPartner
                        ? saveForReviewTooltip
                        : undefined,
                    },
                    {
                      label: t('saveAsDraft', { ns: 'taskbox' }),
                      onClick: onSaveAsDraft,
                    },
                  ]}
                  loading={saveLoading}
                />
              ) : handleDraft ? (
                <WBButton
                  variant="outlined"
                  sx={{ px: 3 }}
                  type="button"
                  loading={saveLoading}
                  onClick={onSaveAsDraft}
                >
                  {t('saveAsDraft', { ns: 'taskbox' })}
                </WBButton>
              ) : null}
              <WBButton
                loading={loading}
                autoFocus
                variant="contained"
                sx={{ px: 3 }}
                type="submit"
                disabled={
                  (showConfirmationCheckbox && !confirmationChecked) || disabled
                }
              >
                {nextButtonText || createButtonText}
              </WBButton>
            </WBFlex>
          </WBFlex>
        </WBFlex>

        <BottomDrawer>
          <WBFlex flexDirection={'column'} gap={2} my={2} mx={3}>
            {showConfirmationCheckbox && (
              <WBBox textAlign="center">
                <WBCheckbox
                  size="small"
                  label={confirmationMessage}
                  checked={confirmationChecked}
                  onChange={(e) => setConfirmationChecked(e.target.checked)}
                  name="confirmDetailsMobile"
                />
              </WBBox>
            )}

            <ButtonWrapper>
              <WBButton
                variant="outlined"
                sx={{ px: 3, flex: 1 }}
                onClick={handleBackToEdit}
              >
                {t('backToEdit', { ns: 'taskbox' })}
              </WBButton>
              {handleDraft && handleSaveForReview ? (
                <WBSplitButton
                  mainAction={{
                    label: t('saveForReview', { ns: 'taskbox' }),
                    onClick: onSaveForReview,
                  }}
                  options={[
                    {
                      label: t('saveForReview', { ns: 'taskbox' }),
                      onClick: onSaveForReview,
                      disabled: false,
                      tooltip: saveForReviewTooltip,
                    },
                    {
                      label: t('saveAsDraft', { ns: 'taskbox' }),
                      onClick: onSaveAsDraft,
                    },
                  ]}
                  loading={saveLoading}
                />
              ) : handleDraft ? (
                <WBButton
                  variant="outlined"
                  sx={{ px: 3, flex: 1 }}
                  type="button"
                  loading={saveLoading}
                  onClick={onSaveAsDraft}
                >
                  {t('saveAsDraft', { ns: 'taskbox' })}
                </WBButton>
              ) : null}
            </ButtonWrapper>

            <WBButton
              fullWidth
              autoFocus
              variant="contained"
              loading={loading}
              sx={{ px: 3 }}
              type="submit"
              disabled={showConfirmationCheckbox && !confirmationChecked}
            >
              {nextButtonText || createButtonText}
            </WBButton>
          </WBFlex>
        </BottomDrawer>
      </>
    );
  }
);
